import { GEM_CONSTANTS, XP_CONSTANTS } from '~/bootcamp/constants/bootcamp.const'
import { LevelActivities, SanityLevelActivities } from '~/types/bootcamp'

import {
  ActivityMenu,
  BootcampActivityEvent,
  BootcampActivityEventId,
  BootcampActivityRoundEventId,
  BootcampKey,
  LevelActionType,
} from '~/bootcamp/types/bootcamp.types'

export function parseBootcampActivityEvents(bootcampEvents: BootcampActivityEventId[]): BootcampActivityEvent[] {
  return bootcampEvents.map((elem) => {
    const event = elem.split('#')
    return {
      bootcampKey: event[0] as BootcampKey,
      moduleId: event[1],
      levelId: event[2],
      levelAction: LevelActionType.Activity,
      activityType: Number(event[4]),
      difficultyLevel: Number(event[5]),
      rewardLevel: Number(event[6]),
      round: Number(event[10] || 0),
    }
  })
}

export function getBootcampActivityEventsForCurrentRound(
  bootcampEvents: BootcampActivityEventId[],
  currentRound: number,
): BootcampActivityEvent[] {
  const events = parseBootcampActivityEvents(bootcampEvents)
  return events.filter((elem) => elem.round === currentRound)
}

export function constructBootcampActivityEventId(
  bootcampKey: BootcampKey,
  moduleId: string,
  level: string,
  activity: number,
  difficulty: number,
  reward: number,
  gemCount: number,
  xp: number,
  round: number,
): BootcampActivityEventId {
  return `${bootcampKey}#${moduleId}#${level}#${LevelActionType.Activity}#${activity}#${difficulty}#${reward}#red#${gemCount}#${xp}#${round}` as const
}

export function formatLevelActivities(levelActivites: SanityLevelActivities[]): LevelActivities {
  const formattedLevelActivities = new Map()

  levelActivites?.forEach((levelActivity) => {
    const currValue = formattedLevelActivities.get(levelActivity.level)

    if (currValue) {
      formattedLevelActivities.set(levelActivity.level, Number(currValue) + Number(levelActivity.numOfActivities))
    } else {
      formattedLevelActivities.set(levelActivity.level, Number(levelActivity.numOfActivities))
    }
  })

  return Object.fromEntries(formattedLevelActivities)
}

// returns gem count in a chest after completing a difficulty level
export function getGemCountInChest(
  bootcampComplexityLevel: number,
  activityWorkloadLevel: number,
  difficultyLevel: number,
): number {
  return getCollectableCountInChest(
    bootcampComplexityLevel,
    activityWorkloadLevel,
    difficultyLevel,
    GEM_CONSTANTS.complexityLevelMultiplier,
    GEM_CONSTANTS.workloadLevelMultiplier,
    GEM_CONSTANTS.difficultyLevelMultiplier,
    GEM_CONSTANTS.minimumGem,
  )
}

// returns xp count in a chest after completing a difficulty level
export function getXPCountInChest(
  bootcampComplexityLevel: number,
  activityWorkloadLevel: number,
  difficultyLevel: number,
): number {
  return getCollectableCountInChest(
    bootcampComplexityLevel,
    activityWorkloadLevel,
    difficultyLevel,
    XP_CONSTANTS.complexityLevelMultiplier,
    XP_CONSTANTS.workloadLevelMultiplier,
    XP_CONSTANTS.difficultyLevelMultiplier,
    XP_CONSTANTS.minimumXP,
  )
}

/**
 * We are using this function for gem and xp count on a chest of difficulty level
 * as the calculation process is same for both
 */
function getCollectableCountInChest(
  bootcampComplexityLevel: number,
  activityWorkloadLevel: number,
  difficultyLevel: number,
  complexityLevelMultiplier: number,
  workloadLevelMultiplier: number,
  difficultyLevelMultiplier: number,
  minimumItem: number,
): number {
  let item = minimumItem
  item += (bootcampComplexityLevel - 1) * complexityLevelMultiplier
  item += (activityWorkloadLevel - 1) * workloadLevelMultiplier
  item += (difficultyLevel - 1) * difficultyLevelMultiplier
  return Math.floor(item)
}

export function checkTagValidity(validDate: string): boolean {
  const present = Date.parse(new Date().toString())
  const otherDate = Date.parse(validDate)
  return present < otherDate
}

export function getCurrentRoundFromRoundEvents(roundEvents: BootcampActivityRoundEventId[]): number {
  let currentRound = 0
  roundEvents.forEach((elem) => {
    const event = elem.split('#')
    currentRound = Math.max(currentRound, Number(event[5] || 0))
  })
  return currentRound
}

export function getBlockWithText(text: string): unknown[] {
  return [
    {
      _key: '835ca5bfdfb6',
      _type: 'block',
      children: [
        {
          _key: 'd697764f10d5',
          _type: 'span',
          marks: [],
          text,
        },
      ],
      markDefs: [],
      style: 'normal',
    },
  ]
}

export function getLevelIndexShift(courseLevels: Record<number, ActivityMenu[]>): 0 | 1 {
  const hasWhyLevel = courseLevels[0]?.[0]?.type === 'why'
  return hasWhyLevel ? 0 : 1
}
